import { useEffect, useRef } from 'react'
import { useSessionStore } from '@stores'

interface Options {
  session: any
  onSelectInput: (input: any) => void
  onBack?: () => void
  /** Scopes wiring to one subtree. Without it the whole document is claimed, so a
   *  host nested inside another must pass its own root to own its clicks. */
  root?: HTMLElement | null
}

/**
 * Wires the `[ctype='path-input']` elements the backend embeds in an `enhanced_mode`
 * step's HTML. Every host that renders that HTML needs this — the funnel, the OMP
 * drawer, the lifeline — or the step's only controls are inert anchors.
 */
export function useEnhancedPathInputs({ session, onSelectInput, onBack, root }: Options) {
  const selectRef = useRef(onSelectInput)
  const backRef = useRef(onBack)
  const popupSession = useSessionStore(s => s.popupSession)

  useEffect(() => {
    selectRef.current = onSelectInput
    backRef.current = onBack
  })

  useEffect(() => {
    if (!session || session.is_step_preview) {
      return
    }

    // Popup HTML references popupSession's input_ids, so both sessions are in the
    // lookup; otherwise popup buttons are treated as unmatched and removed.
    const allInputs = [...((session.valid_inputs as any[]) || []), ...((popupSession?.valid_inputs as any[]) || [])]
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | null = null

    const tryWire = (): boolean => {
      const scope: ParentNode = root ?? document
      const containers = scope.querySelectorAll('.page-content, .inputs')
      if (!containers.length) {
        return false
      }
      let foundAny = false

      containers.forEach(container => {
        if (!(container as any)._delegatedClick) {
          ;(container as any)._delegatedClick = true
          container.addEventListener('click', (e: Event) => {
            const targetEl = e.target as Element | null
            if (!targetEl) {
              return
            }
            const pathInput = targetEl.closest("[ctype='path-input']") as Element | null
            const elemType = pathInput?.getAttribute('elemtype')
            if (elemType === 'button' || elemType === 'link') {
              // Read the store live: a closure captured at bind time holds whichever
              // step was on screen then, not the one the user is clicking.
              const live = useSessionStore.getState()
              const inp = [
                ...((live.session?.valid_inputs as any[]) || []),
                ...((live.popupSession?.valid_inputs as any[]) || []),
              ].find((vi: any) => vi.input_id === Number(pathInput!.getAttribute('input')))
              if (inp) {
                e.preventDefault()
                e.stopPropagation()
                selectRef.current(inp)
                return
              }
            }
            if (targetEl.closest("[elemtype='back-button']") && backRef.current) {
              e.preventDefault()
              e.stopPropagation()
              backRef.current()
            }
          })
        }

        const pathInputs = container.querySelectorAll("[ctype='path-input']")
        if (pathInputs.length) {
          foundAny = true
        }
        pathInputs.forEach(el => {
          const elemType = el.getAttribute('elemtype')
          if (elemType !== 'button' && elemType !== 'link') {
            return
          }
          if (!allInputs.some((vi: any) => vi.input_id === Number(el.getAttribute('input')))) {
            el.remove()
            return
          }
          if (elemType === 'link') {
            el.removeAttribute('target')
            el.removeAttribute('href')
          }
        })

        if ((container as HTMLElement).classList.contains('page-content')) {
          ;(container as HTMLElement).style.display = 'block'
        }
      })

      return foundAny || !session.content
    }

    // Retries for 5s: the HTML lands a tick later, and backend animations after that.
    const loop = () => {
      if (tryWire()) {
        return
      }
      attempts++
      if (attempts < 50) {
        timer = setTimeout(loop, 100)
      }
    }
    loop()

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [session, popupSession, root])
}
